import * as React from "react";
import { toast as sonnerToast } from "sonner";

export interface Toast {
  id: string;
  type: "success" | "error" | "info" | "warning";
  title: string;
  description?: string;
}

interface ToastContextValue {
  toasts: Toast[];
  toast: (toast: Omit<Toast, "id">) => void;
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  // 保留 toasts 状态以保持 API 兼容性，但实际显示由 sonner 处理
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const addToast = React.useCallback((newToast: Omit<Toast, "id">) => {
    const id = Date.now().toString();

    // 使用 sonner 显示 toast
    switch (newToast.type) {
      case "success":
        sonnerToast.success(newToast.title, {
          description: newToast.description,
        });
        break;
      case "error":
        sonnerToast.error(newToast.title, {
          description: newToast.description,
        });
        break;
      case "warning":
        sonnerToast.warning(newToast.title, {
          description: newToast.description,
        });
        break;
      case "info":
      default:
        sonnerToast.info(newToast.title, { description: newToast.description });
        break;
    }

    // 保持内部状态同步（用于可能的 toasts 列表访问）
    const toastItem = { ...newToast, id };
    setToasts((current) => [...current, toastItem]);

    setTimeout(() => {
      setToasts((current) => current.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const dismissToast = React.useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const value = React.useMemo(
    () => ({
      toasts,
      toast: addToast,
      dismiss: dismissToast,
    }),
    [toasts, addToast, dismissToast]
  );

  // 只渲染 children，不再渲染 ToastContainer（由 GlobalToaster/sonner 统一处理）
  return React.createElement(ToastContext.Provider, { value }, children);
}
